import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Alert, Linking, StyleSheet, View } from 'react-native';
import type { AddressType } from '@sg/garnish';
import { Button, HighlightWords, IllusDelivery_1, Image } from '@sg/garnish';
import type { CourierDetails } from '@sg/graphql-schema';
import { FlattenedOrderStatuses } from '@sg/graphql-schema';

import { useTelemetry } from '@order/Telemetry';

export const OrderStatusCourierInfo = (props: OrderStatusCourierInfoProps) => {
  const { orderType, orderStatus, courierName, courierPhone } = props;

  const { formatMessage } = useIntl();
  const { track } = useTelemetry();

  const handlePress = useCallback(async () => {
    track('order_status.contact-courier', { courierPhone: courierPhone ?? '' });

    const url = `tel:${courierPhone}`;
    const isSupported = await Linking.canOpenURL(url);

    if (!isSupported) {
      track('order_status.contact-courier-unsupported');
      Alert.alert(formatMessage(messages.courierInfoError));

      return;
    }

    await Linking.openURL(url);
  }, [courierPhone, formatMessage, track]);

  if (orderType !== 'delivery') return null;

  if (orderStatus !== FlattenedOrderStatuses.Delivering) return null;

  if (!courierName || !courierPhone) return null;

  return (
    <View style={styles.row}>
      <Image source={IllusDelivery_1} style={styles.illustration} />
      <View>
        <View style={styles.courier}>
          <HighlightWords
            size={4}
            text={formatMessage(messages.orderStatusInfoCourier, {
              courier_name: courierName,
            })}
            highlights={[courierName]}
          />
        </View>
        <Button
          testID="order-status-info.contact-courier"
          accessibilityLabel={formatMessage(messages.orderStatusInfoCourierCta)}
          accessibilityHint={formatMessage(messages.orderStatusInfoCourierCta)}
          accessibilityRole="button"
          onPress={handlePress}
        >
          {formatMessage(messages.orderStatusInfoCourierCta)}
        </Button>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  courier: {
    flexDirection: 'row',
    width: 180,
    marginBottom: 8,
  },
  illustration: {
    width: 144,
    height: 144,
  },
});

export type OrderStatusCourierInfoProps = Readonly<{
  orderType?: AddressType;
  orderStatus?: FlattenedOrderStatuses;
  courierName?: CourierDetails['name'];
  courierPhone?: CourierDetails['phone'];
}>;

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  courierInfoError: {
    defaultMessage: 'Something went wrong, try again in a few moments...',
    description: 'Courier Info | Error',
  },
  orderStatusInfoCourier: {
    defaultMessage: '{courier_name} is on the way with your order.',
    description: 'Order Status Info | Courier',
  },
  orderStatusInfoCourierCta: {
    defaultMessage: 'Contact courier',
    description: 'Order Status Info | Courier Cta',
  },
});
